import React from 'react'
import {
  Box,
  Button,
  Field,
  Flex,
  Heading,
  Input,
  Message,
  Modal,
  toast,
} from '@workwhile/ui'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  editBillRateFlowSchema,
  EditBillRateFlowSchema,
} from '../schemas/editBillRate'
import { useEditBillRateFlowContextValue } from '../context/EditBillRateFlowProvider'
import { useBillRatesWorkflowsContextValue } from '../../../context/BillRatesWorkflowsProvider'
import { BillRate } from '../../../types'

export const EditBillRateFlowMain = ({ billRate }: { billRate: BillRate }) => {
  const {
    editBillRateFlow: { isOpen, close },
  } = useBillRatesWorkflowsContextValue()

  const {
    state: {
      mutationState: { isPending, errorMessage },
    },
    actions: { submit, reset: resetMutation },
  } = useEditBillRateFlowContextValue()

  const {
    handleSubmit,
    register,
    formState: { errors },
    reset,
  } = useForm<EditBillRateFlowSchema>({
    resolver: zodResolver(editBillRateFlowSchema),
    values: {
      payRate: billRate.payRate,
      billRate: billRate.billRate,
    },
  })

  const handleClose = () => {
    reset()
    resetMutation()
    close()
  }

  const onSubmit = handleSubmit((data) => {
    submit(
      {
        billRateId: billRate.id,
        payRate: data.payRate,
        billRate: data.billRate,
      },
      {
        onSuccess: () => {
          toast.success('Bill rate updated successfully', {
            position: 'top-center',
          })
          handleClose()
        },
      }
    )
  })

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      position="top"
      loading={isPending}
      showCancel
      customCta={
        <Flex>
          <Button variant="text" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={onSubmit}>
            Save
          </Button>
        </Flex>
      }
    >
      <Box
        as="form"
        onSubmit={(e) => {
          e.preventDefault()
          onSubmit()
        }}
        style={{ width: '100%' }}
      >
        <Heading level={3} fontWeight="400" style={{ margin: 0 }}>
          Edit bill rate
        </Heading>
        <Box mt={4}>Company</Box>
        <Box color="lightText">
          {`${billRate.company.name} (${billRate.company.id})`}
        </Box>
        <Box mt={4}>Position</Box>
        <Box color="lightText">
          {`${billRate.position.name} (${billRate.position.id})`}
        </Box>
        <Box mt={4}>
          <Field label="Pay Rate" error={errors.payRate?.message?.toString()}>
            <Input
              type="number"
              step="0.01"
              min="0.01"
              max="10000"
              aria-label="Pay rate"
              placeholder="e.g. 24.58"
              {...register('payRate', {
                setValueAs: (v) =>
                  v === '' || v == null ? undefined : Number(v),
              })}
            />
          </Field>

          <Field
            label="Charge Rate"
            error={errors.billRate?.message?.toString()}
            // Intentionally disabled. This should work, unsure why it's throwing an error
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            mt={4}
          >
            <Input
              type="number"
              step="0.01"
              min="0.01"
              max="10000"
              aria-label="Charge rate"
              placeholder="e.g. 32.00"
              {...register('billRate', {
                setValueAs: (v) =>
                  v === '' || v == null ? undefined : Number(v),
              })}
            />
          </Field>
        </Box>
        {errorMessage ? (
          <Message variant="error" mt={3} borderRadius="small">
            {errorMessage}
          </Message>
        ) : null}
      </Box>
    </Modal>
  )
}
